import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { ClientContact, VendorContact, CreateClientContactForm, UpdateClientContactForm, CreateVendorContactForm, UpdateVendorContactForm } from '../types';
import { toast } from 'react-hot-toast';

interface UseContactsProps {
  contactType: 'client' | 'vendor';
  parentId: number;
}

export const useContacts = ({ contactType, parentId }: UseContactsProps) => {
  const queryClient = useQueryClient();
  const [selectedContact, setSelectedContact] = useState<ClientContact | VendorContact | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [contactToDelete, setContactToDelete] = useState<ClientContact | VendorContact | null>(null);

  // Check if this is a system admin session
  const isSystemAdmin = typeof window !== 'undefined' && localStorage.getItem('isSystemAdmin') === 'true';

  // Fetch contacts
  const {
    data: contacts = [],
    isLoading: isLoadingContacts,
    error: contactsError
  } = useQuery({
    queryKey: [`${contactType}Contacts`, parentId],
    queryFn: async () => {
      if (contactType === 'client') {
        const response = await apiService.getClientContacts(parentId);
        return response.data || [];
      } else {
        const response = await apiService.getVendorContacts(parentId);
        return response.data || [];
      }
    },
    enabled: !!parentId && !isSystemAdmin // Disable for system admins
  });

  // Fetch primary contact
  const {
    data: primaryContact,
    isLoading: isLoadingPrimary
  } = useQuery({
    queryKey: [`${contactType}PrimaryContact`, parentId],
    queryFn: async () => {
      if (contactType === 'client') {
        const response = await apiService.getPrimaryClientContact(parentId);
        return response.data;
      } else {
        const response = await apiService.getPrimaryVendorContact(parentId);
        return response.data;
      }
    },
    enabled: !!parentId && !isSystemAdmin // Disable for system admins
  });

  // Create contact mutation
  const createContactMutation = useMutation({
    mutationFn: async (data: CreateClientContactForm | CreateVendorContactForm) => {
      if (contactType === 'client') {
        return await apiService.createClientContact(data as CreateClientContactForm);
      } else {
        return await apiService.createVendorContact(data as CreateVendorContactForm);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`${contactType}Contacts`, parentId] });
      queryClient.invalidateQueries({ queryKey: [`${contactType}PrimaryContact`, parentId] });
      toast.success('Contact created successfully');
      setIsModalOpen(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create contact');
    }
  });

  // Update contact mutation
  const updateContactMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UpdateClientContactForm | UpdateVendorContactForm }) => {
      if (contactType === 'client') {
        return await apiService.updateClientContact(id, data as UpdateClientContactForm);
      } else {
        return await apiService.updateVendorContact(id, data as UpdateVendorContactForm);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`${contactType}Contacts`, parentId] });
      queryClient.invalidateQueries({ queryKey: [`${contactType}PrimaryContact`, parentId] });
      toast.success('Contact updated successfully');
      setIsModalOpen(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update contact');
    }
  });

  // Delete contact mutation
  const deleteContactMutation = useMutation({
    mutationFn: async (contactId: number) => {
      if (contactType === 'client') {
        return await apiService.deleteClientContact(contactId);
      } else {
        return await apiService.deleteVendorContact(contactId);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`${contactType}Contacts`, parentId] });
      queryClient.invalidateQueries({ queryKey: [`${contactType}PrimaryContact`, parentId] });
      toast.success('Contact deleted successfully');
      setIsDeleteModalOpen(false);
      setContactToDelete(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete contact');
    }
  });

  // Set primary contact mutation
  const setPrimaryContactMutation = useMutation({
    mutationFn: async (contactId: number) => {
      if (contactType === 'client') {
        return await apiService.setPrimaryClientContact(parentId, contactId);
      } else {
        return await apiService.setPrimaryVendorContact(parentId, contactId);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`${contactType}Contacts`, parentId] });
      queryClient.invalidateQueries({ queryKey: [`${contactType}PrimaryContact`, parentId] });
      toast.success('Primary contact updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to set primary contact');
    }
  });

  // Handlers
  const handleAddNew = useCallback(() => {
    setSelectedContact(null);
    setIsModalOpen(true);
  }, []);

  const handleEdit = useCallback((contact: ClientContact | VendorContact) => {
    setSelectedContact(contact);
    setIsModalOpen(true);
  }, []);

  const handleDelete = useCallback((contact: ClientContact | VendorContact) => {
    setContactToDelete(contact);
    setIsDeleteModalOpen(true);
  }, []);

  const handleSetPrimary = useCallback((contact: ClientContact | VendorContact) => {
    setPrimaryContactMutation.mutate(contact.id);
  }, [setPrimaryContactMutation]);

  const handleSubmit = useCallback((data: CreateClientContactForm | UpdateClientContactForm | CreateVendorContactForm | UpdateVendorContactForm) => {
    if (selectedContact) {
      updateContactMutation.mutate({
        id: selectedContact.id,
        data: data as UpdateClientContactForm | UpdateVendorContactForm
      });
    } else {
      createContactMutation.mutate(data as CreateClientContactForm | CreateVendorContactForm);
    }
  }, [selectedContact, createContactMutation, updateContactMutation]);

  const handleConfirmDelete = useCallback(() => {
    if (contactToDelete) {
      deleteContactMutation.mutate(contactToDelete.id);
    }
  }, [contactToDelete, deleteContactMutation]);

  const isLoading = createContactMutation.isPending || updateContactMutation.isPending || deleteContactMutation.isPending || setPrimaryContactMutation.isPending;

  return {
    // Data
    contacts,
    primaryContact,
    selectedContact,
    
    // Loading states
    isLoadingContacts,
    isLoadingPrimary,
    isLoading,
    
    // Errors
    contactsError,
    
    // Modal states
    isModalOpen,
    isDeleteModalOpen,
    contactToDelete,
    
    // Handlers
    handleAddNew,
    handleEdit,
    handleDelete,
    handleSetPrimary,
    handleSubmit,
    handleConfirmDelete,
    
    // Modal controls
    setIsModalOpen,
    setIsDeleteModalOpen
  };
};
